import { HttpClient } from '@angular/common/http';
import { inject, Injectable, Signal, signal } from '@angular/core';
import { Role, Percentage, AdjustmentType } from '@core/models';
import { environment } from '@env/environment';
import { catchError, Observable, of, tap, throwError } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class MasterDataApiService {
  private readonly baseUrl: string = environment.baseUrl;
  private http = inject(HttpClient);

  private months = signal(new Map<number, string>([
    [1, 'Enero'], [2, 'Febrero'], [3, 'Marzo'], [4, 'Abril'],
    [5, 'Mayo'], [6, 'Junio'], [7, 'Julio'], [8, 'Agosto'],
    [9, 'Septiembre'], [10, 'Octubre'], [11, 'Noviembre'], [12, 'Diciembre']
  ]));
  private years = signal(this.generateYears());
  private entityPercentagesMap = signal(new Map<number, string>());
  private percentageMap = signal(new Map<number, string>());
  private adjustmentTypeMap = signal(new Map<number, string>());
  private statusMap = signal<Map<number, string>>(
    new Map([
      [1, 'Activo'],
      [2, 'Inactivo'],
      [3, 'Bloqueado'],
    ]),
  );

  getPercentageMap(): Signal<Map<number, string>> {
    return this.percentageMap;
  }

  getEntityPercentagesMap(): Signal<Map<number, string>> {
    return this.entityPercentagesMap;
  }

  getAdjustmentTypeMap(): Signal<Map<number, string>> {
    return this.adjustmentTypeMap;
  }

  getStatusMap(): Signal<Map<number, string>> {
    return this.statusMap;
  }

  getMonths(): Signal<Map<number, string>> {
    return this.months;
  }

  getYears(): Signal<Map<number, string>> {
    return this.years;
  }

  fetchPercentages(): Observable<Percentage[]> {
    const url = `${this.baseUrl}/percentages`;

    return this.http.get<Percentage[]>(url).pipe(
      tap((percentages) => this.percentageMap.set(new Map(percentages.map((p) => [p.id, `${p.amount} ${p.unitMeasure}`])))),
      catchError((err) => throwError(() => err.error.message)),
    );
  }

  fetchPercentagesByEntityId(entityId: number | undefined): Observable<Percentage[]> {
    if (entityId) {
      const url = `${this.baseUrl}/percentages/${entityId}`;

      return this.http.get<Percentage[]>(url).pipe(
        tap((percentages) => this.entityPercentagesMap.set(new Map(percentages.map((p) => [p.id, `${p.amount} ${p.unitMeasure}`])))),
        catchError((err) => throwError(() => err.error.message)),
      );
    }

    return of([])
  }

  fetchAdjustmentTypes(): Observable<AdjustmentType[]> {
    const url = `${this.baseUrl}/movements/types`;

    return this.http.get<AdjustmentType[]>(url).pipe(
      tap((types) => this.adjustmentTypeMap.set(new Map(types.map((type) => [type.id, type.name])))),
      catchError((err) => throwError(() => err.error.message)),
    );
  }

  private generateYears(): Map<number, string> {
    const currentYear = new Date().getFullYear();
    const yearMap = new Map<number, string>();
    const yearsToShow = 3; // Últimos 3 años incluyendo el actual
    
    // Orden descendente: del actual hacia atrás
    for (let i = 0; i < yearsToShow; i++) {
      const year = currentYear - i;
      yearMap.set(year, year.toString());
    }
    
    return yearMap;
  }
}
